import React, { useEffect, Fragment, useState } from "react";
import { connect } from "react-redux";
import isEmpty from "lodash/isEmpty";
import { Link } from "react-router-dom";
import { bool, func, arrayOf, number } from "prop-types";

import { selectors as sessionSelectors } from "reducers/session";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import * as configUtils from "util/config";
import GeneralMsg from "pages/_components/GeneralMsg";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import EquivalentTotalBalance from "pages/accounts/_components/EquivalentTotalBalance";
import AccountListFC from "pages/accounts/_components/AccountListFC";
import Options from "pages/accounts/_components/Options";
import AccountAction from "pages/accounts/_components/AccountAction";
import { actions as menuActions } from "reducers/drawer";

import I18n from "pages/_components/I18n";

import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import Button from "pages/_components/Button";
import Image from "pages/_components/Image";
import { isIOS } from "react-device-detect";

const AccountsFC = (props) => {
    const { isDesktop, accounts, fetching, isRequestAvailable, dispatch, fetchingDownload } = props;
    const haveAccounts = accounts.length !== 0;
    const accountsPerBatch = isDesktop ? 10 : 5;
    const [accountsToDisplay, setAccountsToDisplay] = useState([]);

    useEffect(() => {
        const bibankAccountsEnabled = configUtils.getBoolean("bibank.accounts.type.enabled");
        if (bibankAccountsEnabled) {
            dispatch(accountsActions.listAllAccounts());
        } else {
            dispatch(accountsActions.listAccounts());
        }
    }, []);

    useEffect(() => {
        if (accounts?.length) {
            setAccountsToDisplay(accounts.slice(0, accountsPerBatch));
        }
    }, [accounts]);

    const handleClick = () => {
        dispatch(menuActions.menuShow());
    };

    const handleClickPDF = () => {
        dispatch(accountsActions.downloadAccounts("pdf"));
    };

    const displayNextBatch = () => {
        const lastAccountToInclude = accountsToDisplay.length + accountsPerBatch;
        setAccountsToDisplay(
            accounts.slice(0, accounts?.length < lastAccountToInclude ? accounts.length : lastAccountToInclude),
        );
    };

    const renderHeader = () => {
        const renderMenuOption = () => (
            <>
                {isDesktop ? (
                    <>
                        <div className="toolbar-item download" key="exportList">
                            <div className="toolbar-item  add-button w-full" key="addMoreButtonsRight">
                                <Button
                                    className="btn-outline"
                                    onClick={() => handleClickPDF()}
                                    image="images/figma/icon/download.svg"
                                    label="accounts.accountBanner.balances"
                                    disabled={!haveAccounts}
                                    loading={fetchingDownload}
                                />
                            </div>
                        </div>
                        <div className="toolbar-item add-button" key="add">
                            <Link className="btn btn-primary btn-block" to="/accounts/requestForm">
                                <Image src="images/figma/icon/nuevo.svg" className="icon-medium svg-icon" />
                                <I18n
                                    id="accounts.create.title"
                                    componentProps={!isDesktop && { className: "visually-hidden" }}
                                />
                            </Link>
                        </div>
                    </>
                ) : (
                    <div className="toolbar-item commands-mobile">
                        <Button
                            className="toolbar-btn view-options"
                            onClick={handleClick}
                            image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-ios.svg"}
                            label="view more options"
                            block={false}
                            bsStyle="link"
                        />
                    </div>
                )}
            </>
        );
        return (
            <CustomProductHead
                titleId="accounts.accountBanner.title"
                backLinkTo="/desktop"
                className="accounts"
                addLinkToLabel="accounts.create.title"
                addLinkTo="/accounts/requestForm"
                exportList="accounts.accountBanner.balances"
                renderMenuOption={renderMenuOption}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="accounts.accountBanner.title" />
                </div>
            </CustomProductHead>
        );
    };

    return (
        <Fragment>
            <Notification scopeToShow="accounts" />
            {!fetching && renderHeader()}
            <MainContainer showLoader={fetching}>
                <div className="container">
                    {accounts.length ? (
                        <Fragment>
                            {!isEmpty(accounts) && <EquivalentTotalBalance {...props} />}
                            <AccountListFC
                                dataToList={accountsToDisplay}
                                displayNextBatch={displayNextBatch}
                                hasMoreData={accounts?.length > accountsToDisplay.length}
                                {...props}
                            />
                        </Fragment>
                    ) : (
                        <GeneralMsg
                            imagePath="images/coloredIcons/accounts.svg"
                            description={<I18n id="accounts.list.empty" />}
                            callToAction={
                                isRequestAvailable && (
                                    <Link className="btn btn-primary btn-block" to="/accounts/requestForm">
                                        <I18n id="accounts.new" />
                                    </Link>
                                )
                            }
                        />
                    )}
                </div>
            </MainContainer>
            {!isDesktop && (
                <Options>
                    <AccountAction isDesktop={isDesktop} labelKey="accounts.create.title" to="/accounts/requestForm" />
                    <div className="navigational-list-title">
                        <I18n id="accounts.accountBanner.balances" className="navigational-list-title" />
                    </div>
                    <AccountAction
                        isDesktop={isDesktop}
                        labelKey="global.download.pdf"
                        handleClick={() => handleClickPDF()}
                    />
                    <AccountAction isDesktop={isDesktop} labelKey="global.download.xls" />
                </Options>
            )}
        </Fragment>
    );
};

AccountsFC.propTypes = {
    isDesktop: bool,
    isFetchingExport: bool,
    isRequestAvailable: bool,
    accounts: arrayOf({ length: number.isRequired }),
    dispatch: func.isRequired,
    isMobile: bool,
    fetching: bool,
    totalBalanceUSD: number,
    totalBalanceARS: number,
    fetchingDownload: bool,
};

AccountsFC.defaultProps = {
    isFetchingExport: false,
    isDesktop: true,
    isRequestAvailable: false,
    isMobile: false,
    fetching: false,
    accounts: [],
    totalBalanceUSD: "",
    totalBalanceARS: "",
    fetchingDownload: false,
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    totalBalanceUSD: accountsSelectors.getTotalBalanceUSD(state),
    totalBalanceARS: accountsSelectors.getTotalBalanceARS(state),
    fetching: accountsSelectors.getFetching(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
    isRequestAvailable: sessionSelectors
        .getActiveEnvironmentForms(state, "accounts")
        .find(({ idForm }) => idForm === "accountOpening"),
});

export default connect(mapStateToProps)(AccountsFC);
